/**
 * Row presentation for a SAVED report, so reopening one renders the statement the way creating it did.
 *
 * `row_styles` and `row_displays` are client-side only -- the report API neither stores nor returns
 * them -- so a reopened report arrived with no indent, emphasis or heading and its line set rendered
 * flat, while the same report started from its template showed the deck's shape. Both paths now read
 * the presentation from the SAME screen import, rather than the editor growing a second way to draw a
 * line set.
 */

import { getStudioCatalog, importScreen } from 'src/components/star-grid/api/reportEngineClient';
import type { ReportSpec } from 'src/components/star-grid/api/reportEngineTypes';
import { importedRowStyles } from 'src/components/star-grid/utils/reportSpec';

/**
 * `spec` carrying the deck's row presentation. Returned unchanged when the spec already states it, when
 * the report names no template, or when the deck screen cannot be read: presentation must never be the
 * reason a saved report fails to open.
 */
export const withRowPresentation = async (spec: ReportSpec, signal?: AbortSignal): Promise<ReportSpec> => {
  if (!spec.template || (spec.row_styles?.length ?? 0) > 0) return spec;
  try {
    const catalog = await getStudioCatalog(signal);
    const screenId = catalog.templates.find((template) => template.id === spec.template)?.representative_screen_id;
    if (!screenId) return spec;
    const presentation = importedRowStyles(await importScreen(screenId, signal));
    // A row the user RENAMED wins over the deck's own display name, or reopening would revert the
    // rename and the next save would drop it.
    const displays = { ...presentation.row_displays, ...spec.row_labels };
    return { ...spec, ...presentation, ...(Object.keys(displays).length > 0 ? { row_displays: displays } : {}) };
  } catch (error) {
    // A cancelled load must stay cancelled -- swallowing AbortError here would present a stale report
    // as a successful open. Only presentation failures fall back to the plain spec.
    if ((error instanceof DOMException || error instanceof Error) && error.name === 'AbortError') throw error;
    return spec;
  }
};
